import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../common/Button';

const HeaderContainer = styled.header<{ $isScrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: ${({ theme }) => theme.zIndices.sticky};
  transition: all 0.4s ease;
  background-color: ${({ $isScrolled, theme }) => 
    $isScrolled ? theme.colors.white : 'rgba(244, 247, 245, 0.85)'};
  backdrop-filter: ${({ $isScrolled }) => 
    $isScrolled ? 'blur(10px)' : 'blur(5px)'};
  box-shadow: ${({ $isScrolled }) => 
    $isScrolled ? '0 10px 30px rgba(0, 0, 0, 0.05)' : 'none'};
  padding: ${({ $isScrolled, theme }) => 
    $isScrolled ? theme.space.sm : theme.space.md} 0;
  border-bottom: ${({ $isScrolled, theme }) => 
    $isScrolled ? `1px solid ${theme.colors.grey[200]}` : 'none'};
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.space.md};
`;

const Logo = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.dark};
  display: flex;
  align-items: center;
  letter-spacing: 0.02em;
  text-transform: none;
  
  span {
    color: ${({ theme }) => theme.colors.accent};
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: ${({ theme }) => theme.fontWeights.extrabold};
    margin-left: 2px;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 100%;
      height: 3px;
      background-color: ${({ theme }) => theme.colors.accent};
      transform: scaleX(0.7);
      transform-origin: bottom right;
      transition: transform 0.3s ease;
    }
  }
  
  &:hover span::after {
    transform: scaleX(1);
    transform-origin: bottom left;
  }
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const NavLink = styled(Link)<{ $isActive: boolean }>`
  color: ${({ theme, $isActive }) => $isActive ? theme.colors.accent : theme.colors.dark};
  font-weight: ${({ theme, $isActive }) => $isActive ? theme.fontWeights.semibold : theme.fontWeights.medium};
  position: relative;
  padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.sm};
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-family: ${({ theme }) => theme.fonts.accent};
  transition: all 0.3s ease;
  
  &::before, &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    width: 0;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.accent};
    transition: all 0.3s ease;
  }
  
  &::before {
    left: 50%;
  }
  
  &::after {
    right: 50%;
  }
  
  &:hover::before, &:hover::after, 
  &.$isActive::before, &.$isActive::after {
    width: 50%;
  }
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const MobileMenuToggle = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.dark};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  cursor: pointer;
  display: none;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    transform: rotate(90deg);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  height: 100vh;
  background: ${({ theme }) => theme.colors.gradient.primary};
  z-index: ${({ theme }) => theme.zIndices.modal};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.space.xl};
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.1);
`;

const MobileMenuLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
  margin-bottom: ${({ theme }) => theme.space.xl};
  text-align: center;
`;

const MobileNavLink = styled(Link)<{ $isActive: boolean }>`
  color: ${({ theme, $isActive }) => $isActive ? theme.colors.accent : theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme, $isActive }) => $isActive ? theme.fontWeights.semibold : theme.fontWeights.medium};
  letter-spacing: 0.03em;
  text-transform: uppercase;
  position: relative;
  padding: ${({ theme }) => theme.space.xs} 0;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: ${({ $isActive }) => $isActive ? '100%' : '0'};
    height: 2px;
    background-color: ${({ theme }) => theme.colors.accent};
    transition: all 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.space.lg};
  right: ${({ theme }) => theme.space.lg};
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    transform: rotate(90deg);
  }
`;

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  
  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Services' },
    { path: '/about', label: 'About' },
    { path: '/blog', label: 'Blog' },
    { path: '/contact', label: 'Contact' },
  ];
  
  return (
    <HeaderContainer $isScrolled={isScrolled}>
      <HeaderContent>
        <Logo to="/">
          DATA<span>DIGITAL</span>
        </Logo>
        
        <NavLinks>
          {navItems.map((item) => (
            <NavLink 
              key={item.path} 
              to={item.path} 
              $isActive={location.pathname === item.path}
            >
              {item.label}
            </NavLink>
          ))}
          <Button variant="primary" size="small">Get Started</Button>
        </NavLinks>
        
        <MobileMenuToggle onClick={toggleMobileMenu}>
          <FaBars />
        </MobileMenuToggle>
      </HeaderContent>
      
      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
          >
            <CloseButton onClick={toggleMobileMenu}>
              <FaTimes />
            </CloseButton>
            
            <MobileMenuLinks>
              {navItems.map((item) => (
                <MobileNavLink 
                  key={item.path} 
                  to={item.path} 
                  $isActive={location.pathname === item.path}
                  onClick={toggleMobileMenu}
                >
                  {item.label}
                </MobileNavLink>
              ))}
            </MobileMenuLinks>
            
            <Button variant="primary" size="medium">Get Started</Button>
          </MobileMenu>
        )}
      </AnimatePresence>
    </HeaderContainer>
  );
};

export default Header;
